import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { themeGet } from 'styled-system';
import { Container, Box, Hide } from 'roo-ui/components';
import { useDispatch, useSelector } from 'react-redux';
import LocationAutocompleter from 'components/LocationAutocompleter';
import OccupantPicker from 'components/OccupantPicker';
import DatePicker from 'components/DatePicker';
import {
  getQueryLocation,
  getQueryAdults,
  getQueryChildren,
  getQueryInfants,
  getQueryCheckIn,
  getQueryCheckOut,
} from 'store/search/searchSelectors/querySelectors';
import { routeToProperty, updateQuery } from 'store/search/searchActions';
import { Row, Column } from './primitives';
import FixedSearchControls from 'components/FixedSearchControls';

const columnGaps = [12];
const labelOptions = { color: 'greys.alto' };

const SearchControlsWrapper = styled(Box)`
  z-index: ${themeGet('zIndices.searchControls')};
  position: relative;
`;

const ConnectedOccupantPicker = ({ onUpdateQuery }) => {
  const adults = useSelector(getQueryAdults);
  const children = useSelector(getQueryChildren);
  const infants = useSelector(getQueryInfants);
  const occupants = useMemo(() => ({ adults, children, infants }), [adults, children, infants]);

  return <OccupantPicker occupants={occupants} labelOptions={labelOptions} updateQuery={onUpdateQuery} viewThreshold={0} verboseInMobile />;
};

ConnectedOccupantPicker.propTypes = {
  onUpdateQuery: PropTypes.func.isRequired,
};

const ConnectedLocationAutocompleter = ({ onUpdateQuery }) => {
  const location = useSelector(getQueryLocation);
  const dispatch = useDispatch();
  const onRouteToProperty = useCallback((payload) => dispatch(routeToProperty(payload)), [dispatch]);

  return (
    <LocationAutocompleter
      locationName={location}
      labelOptions={labelOptions}
      updateQuery={onUpdateQuery}
      routeToProperty={onRouteToProperty}
    />
  );
};

ConnectedLocationAutocompleter.propTypes = {
  onUpdateQuery: PropTypes.func.isRequired,
};

const ConnectedDatePicker = ({ onUpdateQuery }) => {
  const checkIn = useSelector(getQueryCheckIn);
  const checkOut = useSelector(getQueryCheckOut);
  const selectedDates = useMemo(() => ({ startDate: checkIn, endDate: checkOut }), [checkIn, checkOut]);

  return (
    <DatePicker selectedDates={selectedDates} labelOptions={labelOptions} updateQuery={onUpdateQuery} viewThreshold={0} anchorX="right" />
  );
};

ConnectedDatePicker.propTypes = {
  onUpdateQuery: PropTypes.func.isRequired,
};

const SearchControls = () => {
  const dispatch = useDispatch();
  const onUpdateQuery = useCallback((payload) => dispatch(updateQuery(payload)), [dispatch]);

  return (
    <SearchControlsWrapper bg="white" py={5}>
      <Container>
        <Row gap={columnGaps}>
          <Column gap={columnGaps} cols={[12, 4, 6]} mb={[3, 0]}>
            <ConnectedLocationAutocompleter onUpdateQuery={onUpdateQuery} />
          </Column>

          <Column gap={columnGaps} cols={[7, 6, 4]} data-testid="stay-date-picker">
            <ConnectedDatePicker onUpdateQuery={onUpdateQuery} />
          </Column>

          <Column gap={columnGaps} cols={[5, 2, 2]}>
            <ConnectedOccupantPicker onUpdateQuery={onUpdateQuery} />
          </Column>

          <Hide md lg>
            <FixedSearchControls isListSearch />
          </Hide>
        </Row>
      </Container>
    </SearchControlsWrapper>
  );
};

export default SearchControls;
